
import { ToolId, toolIdToSlug } from './slugs';
import { BlogPost } from '../../data/blogPosts';

export interface PillarSection {
  type: 'how-to' | 'faq' | 'info';
  title: string;
  content: string;
  faqItems?: { question: string; answer: string }[];
  howToSteps?: { name: string; text: string }[];
}

// Define the structure for SEO data
export interface SeoData {
  title: string;
  description: string;
  keywords: string[];
  imageUrl?: string;
  isPillar?: boolean;
  pillarContent?: PillarSection[];
}

export const getCanonicalUrl = (path: string): string => `https://sweetysbakery.com${path}`;

// SEO data for each tool/page
const seoDataMap: Record<string, SeoData> = {
  'home': {
    title: "Sweety's Bakery | Professional Baking Tools & Management Software",
    description: "Elevate your bakery with Sweety's professional tools. Accurate ganache and cake costing calculators, AI recipe generator, and invoice automation solutions designed specifically for ambitious bakery owners.",
    keywords: [
      "free baking calculators", "cake pricing calculator", "recipe scaler", "baking conversion chart",
      "sourdough hydration calculator", "macaron troubleshooting guide", "bakery business tools",
      "cake cost calculator", "pastry chef tools", "baking math", "dough calculator",
      "baking", "baking recipes", "easy baking recipes", "baking for beginners",
      "baking soda", "baking powder", "gluten-free baking",
      "cake recipes", "vanilla cake", "chocolate cake", "red velvet cake", "cupcakes recipe", "frosting recipe",
      "cookie recipes", "chocolate chip cookies", "sugar cookies", "peanut butter cookies", "oatmeal cookies", "no-bake cookies", "cookie dough recipe", "soft cookie recipe",
      "brownie recipe", "fudgy brownies", "easy brownies", "blondies recipe", "brownie mix hacks",
      "bread recipes", "banana bread", "zucchini bread", "pumpkin bread", "sourdough bread", "quick bread recipes",
      "cinnamon rolls", "cheesecake recipe", "apple pie", "muffins recipe", "pastries", "desserts",
      "baking tools", "baking calculators", "baker's percentage"
    ],
    imageUrl: getCanonicalUrl('/opengraph-image.png'),
  },
  'all-tools': {
    title: "Pro Baking Calculators | Accurate Ratios for Bakers | Sweety's Bakery",
    description: "Say goodbye to calculation errors. Use our free calculators to get perfect ganache ratios, convert measurements accurately, and calculate production costs to ensure your project's profitability.",
    keywords: ["baking tools", "bakery calculators", "free tools", "converters"],
    imageUrl: getCanonicalUrl('/tools/all-tools.jpg'),
  },
  'bakers-percentage': {
    title: "Free Baker's Percentage Calculator | Professional Master Formulas",
    description: "Master baker's math with our free calculator. Convert any bread or pastry recipe into scalable percentages. Perfect for sourdough, pizza dough, and professional bakery production.",
    keywords: ["baker's percentage", "dough calculator", "recipe scaling", "baking math", "bakers percentage formula", "bread hydration"],
    imageUrl: getCanonicalUrl('/calculators/bakers-percentage.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Use Baker\'s Percentage',
        content: '<p>Baker\'s percentage is a mathematical system used by professionals to express the ratio of ingredients in a recipe. Unlike standard percentages, the total flour weight is ALWAYS 100%.</p>',
        howToSteps: [
          { name: 'Identify Total Flour Weight', text: 'Add up the weight of all the flours in your recipe. This combined weight is your 100% base.' },
          { name: 'Divide Other Ingredients', text: 'Take the weight of any other ingredient (like water or salt) and divide it by the total flour weight.' },
          { name: 'Multiply by 100', text: 'Multiply that result by 100 to get the Baker\'s Percentage for that specific ingredient.' },
          { name: 'Scale Freely', text: 'Once your recipe is in percentages, you can scale to any batch size by simply deciding how much total flour you want to use, and multiplying it by the percentages.' }
        ]
      },
      {
        type: 'faq',
        title: 'Baker\'s Percentage FAQs',
        content: '<p>Common questions about using Baker\'s Math for bread and pastry.</p>',
        faqItems: [
          { question: 'Why do baker\'s percentages add up to more than 100%?', answer: 'Because flour is always set at 100%. The total percentage of the dough will typically be between 150% and 200%, representing the sum of all ingredient percentages relative to the flour.' },
          { question: 'What is hydration in baking?', answer: 'Hydration is the baker\'s percentage of the total liquid (usually water) relative to the total flour. A 75% hydration dough means there are 75 grams of water for every 100 grams of flour.' }
        ]
      }
    ]
  },
  'baking-pan-converter': {
    title: "Baking Pan Converter & Volume Calculator | Scale Any Cake Size",
    description: "Easily convert recipes between different pan sizes and shapes. Our calculator adjusts ingredient quantities perfectly for round, square, and rectangular pans to prevent overflows.",
    keywords: ["pan converter", "baking pan size", "recipe conversion", "cake pan calculator", "pan volume calculator"],
    imageUrl: getCanonicalUrl('/converters/baking-pan.jpg'),
  },
  'recipe-scaler': {
    title: "Free Recipe Scaler | Batch Scaling for Professional Bakers",
    description: "Precision batch scaling for your recipes. Scale up for large orders or down for single batches with total accuracy. The essential tool for commercial bakeries and home cottage bakers.",
    keywords: ["recipe scaler", "ingredient scaling", "baking adjustments", "quantity converter", "batch calculator", "scale recipe grams"],
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Scale a Recipe Correctly',
        content: '<p>Scaling recipes up or down is not as simple as doing multiplication. To maintain the exact chemistry of the bake, follow these rules:</p>',
        howToSteps: [
          { name: 'Convert to Weights First', text: 'Always convert volume measurements (cups, spoons) to grams before scaling. Volume is inaccurate; a cup of flour can vary by 20% in weight.' },
          { name: 'Calculate Your Multiplier', text: 'Determine the yield you want divided by the yield of the original recipe. So, if you want 40 cookies and the recipe makes 12, your multiplier is 3.33.' },
          { name: 'Multiply Carefully', text: 'Multiply every ingredient weight by your multiplier. Be cautious with strong leavening agents and spices, as they sometimes scale non-linearly.' }
        ]
      },
      {
        type: 'faq',
        title: 'Recipe Scaling FAQs',
        content: '<p>Common questions about scaling baking recipes.</p>',
        faqItems: [
          { question: 'Why did my cake sink when I doubled the recipe?', answer: 'Leavening agents (baking soda/powder) don\'t always scale linearly. Try using only 1.5x the leavening agent when doubling a large cake recipe.' },
          { question: 'Should I scale my baking time and temperature?', answer: 'Temperature typically stays the same, but baking time will increase if the pan size increases. Always rely on visual and structural cues (like a toothpick test) rather than just the timer.' }
        ]
      }
    ]
  },
  'measurement-converter': {
    title: "Baking Measurement Converter | Cups to Grams & Ounces",
    description: "The ultimate baking weight converter. Convert over 50 common ingredients from cups and spoons to precise grams and ounces. Trusted by professional pastry chefs for perfect results.",
    keywords: ["measurement converter", "baking conversion", "grams to cups", "volume to weight", "flour grams per cup"],
    isPillar: true,
  },
  'baking-blog': {
    title: "Baking Blog | Sweety's Bakery",
    description: "Baking tips, recipes, and in-depth tutorials. Your resource for everything baking.",
    keywords: ["baking blog", "pastry recipes", "baking tutorials", "dessert ideas"],
    imageUrl: getCanonicalUrl('/blog/main-image.jpg'),
  },
  'recipe-index': {
    title: "Recipe Index | Sweety's Bakery",
    description: "Browse our full collection of cookies, cakes, breads, and pastries. Find your next bake.",
    keywords: ["recipe index", "baking recipes", "cookie recipes", "cake recipes", "bread recipes"],
    imageUrl: getCanonicalUrl('/images/recipe-index-seo.png'),
  },

  'cake-pricing': {
    title: "Free Cake Pricing Calculator | Profitability Tool for Bakers",
    description: "Eliminate guesswork and price for profit. Calculate exact costs for ingredients, labor, overhead, and delivery. Generate professional quotes and protect your bakery's margins.",
    keywords: ["cake pricing calculator", "cake cost calculator", "price my cake", "bakery pricing", "food cost calculator", "bakery profit margin"],
    imageUrl: getCanonicalUrl('/calculators/cake-pricing.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Price a Cake for Profit',
        content: '<p>Pricing cakes correctly is the difference between a thriving bakery and an expensive hobby. Our calculator takes the guesswork out of the process.</p>',
        howToSteps: [
          { name: 'Calculate Ingredient Costs', text: 'Enter the exact cost of every ingredient used in your cake, filling, and frosting. Don\'t forget the board and box!' },
          { name: 'Determine Your Hourly Wage', text: 'Decide what your time is worth. Enter your hourly rate and the total time spent baking, decorating, and consulting.' },
          { name: 'Factor in Overhead', text: 'Include indirect costs like electricity, water, insurance, and marketing. A common method is adding a 15-20% overhead fee.' },
          { name: 'Add Your Profit Margin', text: 'Profit is not your wage! It is the money reinvested into the business. Standard bakery profit margins aim for 15-20%.' }
        ]
      },
      {
        type: 'faq',
        title: 'Cake Pricing FAQs',
        content: '<p>Frequently asked questions about pricing cakes for home and commercial bakers.</p>',
        faqItems: [
          { question: 'Why are custom cakes so expensive?', answer: 'Custom cakes require hours of dedicated labor, specialized skills, premium ingredients, and overhead costs which supermarket cakes do not have.' },
          { question: 'How much should I charge for a 6 inch cake?', answer: 'It depends entirely on your costs, location, and the design. A basic 6-inch buttercream cake might start around $50-$75, while a high-end custom design could be $150+.' }
        ]
      }
    ]
  },
  'baking-bucket-list': {
    title: "Baking Bucket List | Sweety's Bakery",
    description: "Track your baking journey with challenges for every skill level.",
    keywords: ["baking bucket list", "baking challenges", "baking goals"],
    imageUrl: getCanonicalUrl('/tools/bucket-list.jpg'),
  },
  'digital-notebook': {
    title: "Free Digital Notebook | Sweety's Bakery",
    description: "Keep all your baking notes, recipes, and ideas organized in one place with our free digital notebook.",
    keywords: ["digital notebook", "baking notes", "recipe organizer", "baking journal"],
    imageUrl: getCanonicalUrl('/tools/digital-notebook.jpg'),
  },
  'oven-temp-converter': {
    title: "Free Oven Temperature Converter | Sweety's Bakery",
    description: "Convert oven temperatures between Celsius, Fahrenheit, and gas marks instantly for perfect baking.",
    keywords: ["oven temperature converter", "celsius to fahrenheit", "gas mark converter", "baking temperature"],
    imageUrl: getCanonicalUrl('/converters/oven-temp.jpg'),
  },
  'fondant-calculator': {
    title: "Free Fondant Quantity | Sweety's Bakery",
    description: "Calculate exactly how much fondant you need to cover any cake size with our free calculator.",
    keywords: ["fondant calculator", "fondant coverage", "cake fondant", "fondant amount"],
    imageUrl: getCanonicalUrl('/calculators/fondant.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Calculate Fondant for Any Cake',
        content: '<p>Getting the right amount of fondant ensures a smooth finish without wasting expensive materials. Use these guidelines for perfect coverage.</p>',
        howToSteps: [
          { name: 'Measure Cake Height', text: 'Standard charts assume a 4-inch tall cake. If your cake is taller (like modern barrel cakes), you need to measure the exact height and diameter.' },
          { name: 'Roll to the Right Thickness', text: 'Professional decorators roll fondant to about 1/8 to 1/4 inch thickness. Rolling too thick wastes fondant and creates an unpleasant eating experience.' },
          { name: 'Factor in the Drape', text: 'Always calculate an extra 2-3 inches of diameter to give yourself enough "drape" to prevent tearing the edges while smoothing.' }
        ]
      },
      {
        type: 'faq',
        title: 'Fondant Coverage FAQs',
        content: '<p>Answers to common questions about working with rolled fondant.</p>',
        faqItems: [
          { question: 'Do I include the cake board in my measurements?', answer: 'Yes, if you are covering the cake board in fondant as well, you must add the diameter of the board to your total needed amount.' },
          { question: 'Why does my fondant tear at the top edges?', answer: 'Usually, this is caused by not rolling out enough fondant to create a generous drape, causing the weight of the fondant to pull down and tear.' }
        ]
      }
    ]
  },
  'frosting-color-mixer': {
    title: "Free Frosting Color Mixer | Sweety's Bakery",
    description: "Get perfect frosting colors every time. Mix food coloring ratios for buttercream and royal icing.",
    keywords: ["frosting color mixer", "food coloring chart", "buttercream colors", "icing color guide"],
    imageUrl: getCanonicalUrl('/tools/color-mixer.jpg'),
  },
  'dough-hydration': {
    title: "Free Dough Hydration | Sweety's Bakery",
    description: "Calculate dough hydration percentage for perfect bread, pizza, and sourdough recipes.",
    keywords: ["dough hydration calculator", "bread hydration", "sourdough hydration", "baker's percentage"],
    imageUrl: getCanonicalUrl('/calculators/dough-hydration.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Understand Dough Hydration',
        content: '<p>Hydration is the single most important factor determining crumb structure, crust, and ease of handling in bread making.</p>',
        howToSteps: [
          { name: 'Define the Ratio', text: 'Hydration is simply the weight of liquids divided by the weight of flour. A 70% hydration means 70g water for 100g flour.' },
          { name: 'Adjust for Flour Types', text: 'Whole wheat and rye flours absorb more water than bread flour. A recipe that feels perfect at 75% hydration with white flour might feel stiff with whole wheat.' },
          { name: 'Watch Your Inclusions', text: 'If you add liquid ingredients like honey, milk, or purees, these factor into the total hydration of the dough.' }
        ]
      },
      {
        type: 'faq',
        title: 'Dough Hydration FAQs',
        content: '<p>Expert answers for managing wet doughs.</p>',
        faqItems: [
          { question: 'What is a good hydration for sourdough?', answer: 'For beginners, 65-70% hydration is recommended. Advanced bakers pushing for an extremely open, airy crumb often use 80-85% hydration.' },
          { question: 'Why is my high hydration dough a sticky soup?', answer: 'High hydration doughs require strong flour (high protein) and proper gluten development techniques like stretch-and-folds or slap-and-folds early in fermentation.' }
        ]
      }
    ]
  },
  'temperature-converter': {
    title: "Free Temperature Converter | Sweety's Bakery",
    description: "Convert temperatures between Celsius, Fahrenheit, and Kelvin instantly.",
    keywords: ["temperature converter", "celsius to fahrenheit", "fahrenheit to celsius"],
    imageUrl: getCanonicalUrl('/converters/temperature.jpg'),
  },
  'yeast-converter': {
    title: "Free Yeast Converter | Sweety's Bakery",
    description: "Convert between active dry yeast, instant yeast, and fresh yeast for perfect bread recipes.",
    keywords: ["yeast converter", "yeast conversion", "active dry yeast", "instant yeast"],
    imageUrl: getCanonicalUrl('/converters/yeast.jpg'),
  },
  'baker-schedule': {
    title: "Free Baker Schedule | Sweety's Bakery",
    description: "Plan your baking schedule with our free checklist and timeline tool for perfect results.",
    keywords: ["baker schedule", "baking timeline", "baking checklist", "baking planner"],
    imageUrl: getCanonicalUrl('/tools/baker-schedule.jpg'),
  },
  'macaron-troubleshooting': {
    title: "Free Macaron Troubleshooting Guide | Sweety's Bakery",
    description: "Fix common macaron problems: cracked shells, no feet, hollows. Visual guide with expert tips.",
    keywords: ["macaron troubleshooting", "macaron problems", "macaron feet", "macaron shells"],
    imageUrl: getCanonicalUrl('/guides/macaron-troubleshooting.jpg'),
  },
  'buttercream-calculator': {
    title: "Free Buttercream Quantity | Sweety's Bakery",
    description: "Calculate exactly how much buttercream you need for any cake size including filling and piping.",
    keywords: ["buttercream calculator", "frosting calculator", "buttercream amount", "cake frosting"],
    imageUrl: getCanonicalUrl('/calculators/buttercream.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Calculate Buttercream for Cakes',
        content: '<p>Don\'t run out of frosting mid-piping. Standardize your buttercream math using these simple professional rules.</p>',
        howToSteps: [
          { name: 'Calculate the Filling', text: 'Plan for roughly 1/2 cup of buttercream for every layer of filling in a standard 8-inch cake.' },
          { name: 'Account for the Crumb Coat', text: 'A crumb coat uses a very thin layer of frosting to lock in crumbs. You typically need about 1 cup of frosting specifically for the crumb coat on an 8-inch cake.' },
          { name: 'Calculate Final Coat & Piping', text: 'The outer layer requires significantly more. Add an extra 2 cups for a smooth outer layer, plus 1 extra cup if you plan to pipe borders or rosettes.' }
        ]
      },
      {
        type: 'faq',
        title: 'Buttercream Yield FAQs',
        content: '<p>Most common questions about frosting quantities.</p>',
        faqItems: [
          { question: 'Does Swiss Meringue Buttercream yield the same as American Buttercream?', answer: 'No. Swiss Meringue is aerated heavily by the egg whites and yields significantly more volume by weight compared to dense American Buttercream.' },
          { question: 'What do I do with leftover buttercream?', answer: 'Buttercream freezes beautifully! Place leftovers in an airtight container or zip-top bag and freeze for up to 3 months. Let it come to room temperature and re-whip before using.' }
        ]
      }
    ]
  },
  'ganache-calculator': {
    title: "Free Ganache Ratios & Quantity Calculator | Perfect Drips & Fillings",
    description: "Find the exact chocolate-to-cream ratio for every ganache type. Calculate quantities for covering cakes, making drips, or filling truffles with white, milk, and dark chocolate.",
    keywords: ["ganache calculator", "chocolate ganache", "ganache ratio", "chocolate to cream ratio", "cake drip calculator"],
    imageUrl: getCanonicalUrl('/calculators/ganache.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Master Ganache Ratios',
        content: '<p>Chocolate ganache behaves entirely differently depending on the cocoa butter content of the chocolate you use.</p>',
        howToSteps: [
          { name: 'Identify Your Chocolate', text: 'Dark chocolate (60%+ cocoa) requires more liquid. Milk chocolate requires less liquid. White chocolate requires the least amount of liquid to set firmly.' },
          { name: 'Choose Your Application', text: 'For a pourable drip, you need a 1:1 ratio (by weight) for dark chocolate. For a firm cake filling or frosting under fondant, use a 2:1 ratio.' },
          { name: 'Weigh Everything', text: 'Never use cup measurements for ganache. Because chocolate chips and callets take up different volumes, you must weigh your chocolate and heavy cream in grams.' }
        ]
      },
      {
        type: 'faq',
        title: 'Ganache Troubleshooting FAQs',
        content: '<p>Expert answers for split or grainy ganache.</p>',
        faqItems: [
          { question: 'Why did my ganache split or become oily?', answer: 'Ganache splits when the emulsion breaks, usually due to the cream being too hot, or over-stirring. You can fix it by vigorously whisking in a splash of warm milk.' },
          { question: 'How long does ganache take to set?', answer: 'Pourable ganache for drips is ready at 90°F (32°C). For firm whipping ganache, it must sit at room temperature for at least 4-6 hours or overnight.' }
        ]
      }
    ]
  },
  'dough-temperature': {
    title: "Dough Temperature (DDT) | Sweety's Bakery",
    description: "Calculate the exact water temperature needed for perfect dough fermentation.",
    keywords: ["dough temperature", "desired dough temperature", "DDT calculator", "bread dough temp"],
    imageUrl: getCanonicalUrl('/calculators/dough-temperature.jpg'),
    isPillar: true,
  },
  'flavor-pairing-guide': {
    title: "Free Flavor Pairing Guide | Sweety's Bakery",
    description: "Discover what flavors go well together with our creative guide for cakes, cookies, and desserts.",
    keywords: ["flavor pairing", "flavor combinations", "flavor wheel", "dessert flavors"],
    imageUrl: getCanonicalUrl('/guides/flavor-pairing.jpg'),
  },
  'ingredient-substitutions': {
    title: "Free Ingredient Substitutions Guide | Sweety's Bakery",
    description: "Find common ingredient swaps and substitutions for baking emergencies.",
    keywords: ["ingredient substitutions", "baking substitutes", "ingredient swaps", "baking alternatives"],
    imageUrl: getCanonicalUrl('/guides/substitutions.jpg'),
  },
  'allergen-guide': {
    title: "Free Allergen Guide | Sweety's Bakery",
    description: "Comprehensive guide to common allergens in baking and how to avoid them.",
    keywords: ["allergen guide", "food allergies", "baking allergens", "allergen-free baking"],
    imageUrl: getCanonicalUrl('/guides/allergen.jpg'),
  },
  'ingredient-glossary': {
    title: "Free Ingredient Glossary | Sweety's Bakery",
    description: "Complete glossary of baking ingredients with descriptions and usage tips.",
    keywords: ["ingredient glossary", "baking ingredients", "ingredient guide"],
    imageUrl: getCanonicalUrl('/guides/ingredients.jpg'),
  },
  'management-dashboard': {
    title: "Free Bakery Management Dashboard | Sweety's Bakery",
    description: "Streamline your bakery operations with inventory tracking, prep lists, and waste logging.",
    keywords: ["bakery management", "bakery dashboard", "inventory management", "bakery operations"],
    imageUrl: getCanonicalUrl('/tools/management-dashboard.jpg'),
  },
  'order-management': {
    title: "Free Bakery Order Management | Sweety's Bakery",
    description: "Manage all your customer orders in one place. Track status, details, and delivery dates.",
    keywords: ["order management", "bakery orders", "order tracking", "customer orders"],
    imageUrl: getCanonicalUrl('/tools/order-management.jpg'),
  },
  'shopping-list': {
    title: "Free Shopping List | Sweety's Bakery",
    description: "Create and manage your baking shopping lists with our free tool.",
    keywords: ["shopping list", "grocery list", "baking ingredients list"],
    imageUrl: getCanonicalUrl('/tools/shopping-list.jpg'),
  },
  'recipe-box': {
    title: "Free Recipe Box | Sweety's Bakery",
    description: "Save and organize your favorite baking recipes in one convenient place.",
    keywords: ["recipe box", "recipe organizer", "save recipes", "recipe collection"],
    imageUrl: getCanonicalUrl('/tools/recipe-box.jpg'),
  },
  'dessert-calculator': {
    title: "Free Dessert Calculator | Sweety's Bakery",
    description: "Calculate serving sizes and portions for desserts and baked goods.",
    keywords: ["dessert calculator", "serving calculator", "portion calculator"],
    imageUrl: getCanonicalUrl('/calculators/dessert.jpg'),
  },
  'cost-per-recipe': {
    title: "Free Cost Per Recipe | Sweety's Bakery",
    description: "Calculate the exact cost of any recipe including ingredients and overhead.",
    keywords: ["recipe cost calculator", "food cost", "recipe pricing", "ingredient cost"],
    imageUrl: getCanonicalUrl('/calculators/cost-per-recipe.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Calculate True Food Cost',
        content: '<p>Don\'t guess your margins. Use this method to find the exact cost of producing a single batch of any recipe.</p>',
        howToSteps: [
          { name: 'Determine Yield', text: 'Define exactly how many cookies, cupcakes, or kg of dough a single batch of your recipe produces.' },
          { name: 'Cost Every Ingredient', text: 'Take the price of bulk ingredients and divide by the weight to find the cost-per-gram. Multiply this by the grams used in the recipe.' },
          { name: 'Add Hidden Costs', text: 'Food cost isn\'t just ingredients. Add the cost of piping bags, parchment paper, and packaging used for that single batch.' }
        ]
      },
      {
        type: 'faq',
        title: 'Recipe Costing FAQs',
        content: '<p>Expert answers for managing bakery margins.</p>',
        faqItems: [
          { question: 'What is a good food cost percentage?', answer: 'For bakeries, a food cost percentage between 20% and 30% is ideal. If a cookie costs $1 to make, it should sell for $3.33 to $5.00.' },
          { question: 'How often should I update my recipe costs?', answer: 'Ingredient prices fluctuate constantly (especially dairy and eggs). We recommend auditing your highest-volume recipes every quarter.' }
        ]
      }
    ]
  },
  'baking-tutorials': {
    title: "Free Baking Tutorials | Sweety's Bakery",
    description: "Learn from the pros with our curated collection of the best baking video tutorials.",
    keywords: ["baking tutorials", "baking videos", "how to bake", "baking lessons"],
    imageUrl: getCanonicalUrl('/tutorials/baking.jpg'),
  },
  'relaxing-videos': {
    title: "Relaxing Baking Videos | Sweety's Bakery",
    description: "Enjoy calming, satisfying baking videos perfect for relaxation and inspiration.",
    keywords: ["relaxing videos", "satisfying baking", "asmr baking", "calming videos"],
    imageUrl: getCanonicalUrl('/videos/relaxing.jpg'),
  },
  'drinks-videos': {
    title: "Drinks & Beverages Videos | Sweety's Bakery",
    description: "Learn to make delicious drinks and beverages to pair with your baked goods.",
    keywords: ["drinks videos", "beverage recipes", "coffee tutorials", "drink making"],
    imageUrl: getCanonicalUrl('/videos/drinks.jpg'),
  },
  'butterfat-converter': {
    title: "Free Butterfat Converter | Sweety's Bakery",
    description: "Convert between different butterfat percentages for cream and dairy products.",
    keywords: ["butterfat converter", "cream converter", "dairy fat percentage"],
    imageUrl: getCanonicalUrl('/converters/butterfat.jpg'),
  },
  'hydration-converter': {
    title: "Free Hydration Converter | Sweety's Bakery",
    description: "Convert dough hydration percentages and calculate water amounts for bread recipes.",
    keywords: ["hydration converter", "dough hydration", "bread hydration calculator"],
    imageUrl: getCanonicalUrl('/converters/hydration.jpg'),
  },
  'inventory-management': {
    title: "Free Inventory Management | Sweety's Bakery",
    description: "Track and manage your bakery inventory with our free tool.",
    keywords: ["inventory management", "stock tracking", "bakery inventory"],
    imageUrl: getCanonicalUrl('/tools/inventory.jpg'),
  },
  'recipe-scaling-costing': {
    title: "Recipe Scaling & Costing | Sweety's Bakery",
    description: "Professional recipe scaling and costing tool for bakeries and home bakers.",
    keywords: ["recipe scaling", "recipe costing", "food cost", "bakery math"],
    imageUrl: getCanonicalUrl('/calculators/recipe-scaling.jpg'),
    isPillar: true,
    pillarContent: [
      {
        type: 'how-to',
        title: 'How to Scale Up Bakery Production safely',
        content: '<p>Scaling recipes from a domestic to commercial level requires specific math to avoid chemical imbalance.</p>',
        howToSteps: [
          { name: 'Convert to Weights', text: 'Volumetric scaling (cups and spoons) multiplies errors. Before scaling anything, convert the entire original recipe to grams.' },
          { name: 'Find the Conversion Factor', text: 'Divide your Desired Yield by the Original Yield. Multiply every ingredient by this exact number.' },
          { name: 'Adjust Leavening Agents Carefully', text: 'Baking powder, soda, and yeast do not scale perfectly linearly at massive volumes. You may need to reduce leavening agents by 10-20% when scaling up past 4x.' }
        ]
      }
    ]
  },
  'default': {
    title: "Free Baking Tools | Sweety's Bakery",
    description: "Free tools and calculators to help with your baking projects.",
    keywords: ["baking", "tools", "calculator", "free"],
    imageUrl: getCanonicalUrl('/placeholder-default.jpg'),
  }
};

// Function to get SEO data for a given tool
export const getToolSeoData = (toolId: ToolId): SeoData => {
  return seoDataMap[toolId] || seoDataMap.default;
};

// Function to update the JSON-LD schema in the document head
// Function to update the JSON-LD schema in the document head
export const updateSchema = (toolId: ToolId, seoData: SeoData, post: BlogPost | null) => {
  if (typeof window === 'undefined') return;

  let schema = {};
  const url = getCanonicalUrl(toolIdToSlug(toolId));

  // Helper to extract list items from HTML content based on a header
  const extractListFromHtml = (html: string, headerKeywords: string[]): string[] => {
    if (typeof window === 'undefined') return []; // Ensure we are on client side
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const headers = Array.from(doc.querySelectorAll('h1, h2, h3, h4, h5, h6'));

      const targetHeader = headers.find(header =>
        headerKeywords.some(keyword => header.textContent?.toLowerCase().includes(keyword.toLowerCase()))
      );

      if (targetHeader) {
        let sibling = targetHeader.nextElementSibling;
        while (sibling) {
          if (sibling.tagName === 'UL' || sibling.tagName === 'OL') {
            return Array.from(sibling.children).map(li => li.textContent?.trim() || '').filter(Boolean);
          }
          sibling = sibling.nextElementSibling;
        }
      }
    } catch (e) {
      console.error('Error parsing HTML for schema:', e);
    }
    return [];
  };

  if ((toolId as string) !== 'default' && !post) {
    // Specialized SoftwareApplication Schema for Tools
    const isCalculator =
      seoData.title.toLowerCase().includes('calculator') ||
      seoData.title.toLowerCase().includes('converter') ||
      toolId.includes('calculator') ||
      toolId.includes('converter') ||
      toolId.includes('generator') ||
      toolId === 'recipe-scaler';

    if (isCalculator) {
      schema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": seoData.title,
        "description": seoData.description,
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Any",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "author": {
          "@type": "Organization",
          "name": "Sweety's Bakery",
          "url": "https://sweetysbakery.com",
          "telephone": "07 19 43 28 24"
        },
        "featureList": seoData.description,
        ...(toolId === 'recipe-scaler' && { "applicationCategory": "UtilityApplication" })
      };
    } else {
      // Fallback for other tools
      schema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "url": url,
        "name": seoData.title,
        "description": seoData.description,
        "publisher": {
          "@type": "Organization",
          "name": "Sweety's Bakery",
          "telephone": "07 19 43 28 24",
          "logo": {
            "@type": "ImageObject",
            "url": getCanonicalUrl("/icon.png")
          }
        }
      };
    }
  } else if (post) {
    const isRecipe = post.type === 'recipe';
    const canonicalId = getCanonicalUrl(`/${isRecipe ? 'recipes' : 'blog'}/${post.slug}`);

    if (isRecipe) {
      const ingredients = extractListFromHtml(post.content, ['ingredients']);
      const instructions = extractListFromHtml(post.content, ['instructions', 'directions', 'method']);

      schema = {
        "@context": "https://schema.org",
        "@type": "Recipe",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalId
        },
        "name": post.title.split('|')[0].trim(),
        "image": [
          getCanonicalUrl(post.imageUrl)
        ],
        "author": {
          "@type": "Organization",
          "name": "Sweety's Bakery"
        },
        "datePublished": post.date,
        "description": post.excerpt,
        "recipeIngredient": ingredients,
        "recipeInstructions": instructions.map(step => ({
          "@type": "HowToStep",
          "text": step
        })),
        "recipeCategory": post.category,
        // Enhanced fields for Google Rich Results
        ...(post.recipeData?.prepTime && { "prepTime": post.recipeData.prepTime }),
        ...(post.recipeData?.cookTime && { "cookTime": post.recipeData.cookTime }),
        ...(post.recipeData?.totalTime && { "totalTime": post.recipeData.totalTime }),
        ...(post.recipeData?.recipeYield && { "recipeYield": post.recipeData.recipeYield }),
        ...(post.recipeData?.calories && {
          "nutrition": {
            "@type": "NutritionInformation",
            "calories": `${post.recipeData.calories} calories`
          }
        }),
        ...(post.recipeData?.rating && {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": post.recipeData.rating.ratingValue,
            "ratingCount": post.recipeData.rating.ratingCount
          }
        })
      };
    } else {
      // Schema for a blog post
      schema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalId
        },
        "headline": post.title.split('|')[0].trim(),
        "description": post.excerpt,
        "image": {
          "@type": "ImageObject",
          "url": getCanonicalUrl(post.imageUrl),
          "caption": post.imageTitle || post.title,
          "name": post.imageAlt || post.title
        },
        "author": {
          "@type": "Organization",
          "name": "Sweety's Bakery",
          "url": "https://sweetysbakery.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Sweety's Bakery",
          "logo": {
            "@type": "ImageObject",
            "url": getCanonicalUrl("/icon.png")
          }
        },
        "datePublished": post.date,
        "articleSection": post.category,
        "keywords": post.keywords
      };
    }
  } else {
    // Generic schema for a tool or webpage
    schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": url,
      "name": seoData.title,
      "description": seoData.description,
      "publisher": {
        "@type": "Organization",
        "name": "Sweety's Bakery",
        "telephone": "07 19 43 28 24",
        "logo": {
          "@type": "ImageObject",
          "url": getCanonicalUrl("/placeholders/logo.png")
        }
      }
    };
  }

  // Remove existing schema script if it exists
  const existingScript = document.getElementById('json-ld-schema');
  if (existingScript) {
    existingScript.remove();
  }

  // Add new schema script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'json-ld-schema';
  script.innerHTML = JSON.stringify(schema);
  document.head.appendChild(script);
};
// End of file
